import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useCampanella, quandoNotifica } from '../features/NotificationPoller';
import Icona from '../cliente/Icone';
import RicercaGlobale, { useScorciatoiaCerca } from './RicercaGlobale';
import { usePersone } from './Persone';
import './staff.css';

// Contenitore delle pagine dello staff nel nuovo stile (bozze design/admin-oggi-v4.html e agenda-v9.html):
// barra in alto con Oggi · Clienti · Contatti · Documenti · Agenzie · Agenda, ricerca (⌘K), campanella e profilo; sotto i
// 1000px una barra in basso. Le pagine ancora nel vecchio stile stanno nel contenitore di sempre.

export const vociStaff = (userType) => {
  const oggi = userType === 'admin' ? '/admin' : '/collaboratore';
  const voci = [
    { to: oggi, etichetta: 'Oggi', icona: 'orologio', attiva: (p) => p === oggi },
    { to: '/admin/clienti', etichetta: 'Clienti', icona: 'persone', attiva: (p) => p.startsWith('/admin/clienti') || p.startsWith('/clienti') },
    // I contatti (chi non è ancora cliente) li vedono admin e collaboratori: ognuno i propri, lo decide il server
    { to: '/admin/contatti', etichetta: 'Contatti', icona: 'persone', attiva: (p) => p.startsWith('/admin/contatti') },
    { to: '/admin/documenti', etichetta: 'Documenti', icona: 'documento', attiva: (p) => p.startsWith('/admin/documenti') || p.startsWith('/admin/archivio') },
    // Le case da proporre: le vede tutto lo staff, ognuno per i suoi clienti (il filtro lo fa il server)
    { to: '/admin/da-proporre', etichetta: 'Proposte', icona: 'cuore', attiva: (p) => p.startsWith('/admin/da-proporre') },
  ];
  if (userType === 'admin') voci.push({ to: '/admin/agenzie', etichetta: 'Agenzie', icona: 'edificio', attiva: (p) => p.startsWith('/admin/agenzie') || p.startsWith('/agenzie') });
  // I prospetti del mutuo sono dati economici di una famiglia: li vede solo l'admin, e il server risponde 403
  // a tutti gli altri — questa riga nasconde la voce, non protegge niente.
  if (userType === 'admin') voci.push({ to: '/admin/mutui', etichetta: 'Mutui', icona: 'euro', attiva: (p) => p.startsWith('/admin/mutui') });
  // Social: pubblica su un profilo pubblico. Anche qui la riga nasconde la voce, il 403 lo dà il server.
  if (userType === 'admin') voci.push({ to: '/admin/social', etichetta: 'Social', icona: 'stella', attiva: (p) => p.startsWith('/admin/social') });
  voci.push({ to: '/admin/agenda', etichetta: 'Agenda', icona: 'calendario', attiva: (p) => p.startsWith('/admin/agenda') });
  return voci;
};

const chiudiFuori = (aperto, chiudi, ref) => {
  if (!aperto) return undefined;
  const fuori = (e) => { if (ref.current && !ref.current.contains(e.target)) chiudi(); };
  const esc = (e) => { if (e.key === 'Escape') chiudi(); };
  document.addEventListener('mousedown', fuori);
  document.addEventListener('keydown', esc);
  return () => { document.removeEventListener('mousedown', fuori); document.removeEventListener('keydown', esc); };
};

// Una notifica è un link alla sua pagina (url dal server), come quella del cliente: il clic porta lì e chiude la
// tendina. Senza url si apre "Oggi" (ripiego); un indirizzo completo (http…) esce dall'app con un <a>.
export const VoceNotifica = ({ n, ripiego, chiudi }) => {
  const dove = n.url || ripiego;
  const dentro = (
    <>
      <b>{n.title}</b>
      {n.body && <span>{n.body}</span>}
      {/* l'ora di Roma, come le Novità e la campanella del cliente: con il telefono su un altro fuso
          new Date(...).toLocaleString diceva il giorno e l'ora del telefono */}
      <small>{quandoNotifica(n.sent_at)}</small>
    </>
  );
  return (
    <li>
      {/^https?:\/\//i.test(dove)
        ? <a href={dove} onClick={chiudi} data-testid="voce-notifica">{dentro}</a>
        : <Link to={dove} onClick={chiudi} data-testid="voce-notifica">{dentro}</Link>}
    </li>
  );
};

// La tendina dice quando sta caricando e quando il server non risponde ("Riprovate"): mai un falso "Nessuna notifica".
const Campanella = () => {
  const { userType } = useAuth();
  const { unreadCount, showDropdown, setShowDropdown, notifiche, statoElenco, riprova, handleOpen } = useCampanella();
  const ref = useRef(null);
  const ripiego = vociStaff(userType)[0].to;
  const chiudi = () => setShowDropdown(false);
  useEffect(() => chiudiFuori(showDropdown, () => setShowDropdown(false), ref), [showDropdown, setShowDropdown]);
  return (
    <div className="menu-ancora" ref={ref}>
      <button type="button" className={`ib${unreadCount > 0 ? ' bell' : ''}`} onClick={handleOpen}
        aria-label={unreadCount > 0 ? `Notifiche: ${unreadCount} ${unreadCount === 1 ? 'nuova' : 'nuove'}` : 'Notifiche'} aria-expanded={showDropdown} data-testid="notification-bell">
        <Icona nome="campanella" />
      </button>
      {showDropdown && (
        <div className="tendina notifiche" role="dialog" aria-label="Notifiche">
          <h3>Notifiche</h3>
          {statoElenco === 'attesa' ? <p className="vuoto attesa-notifiche" role="status">Carico le notifiche…</p>
            : statoElenco === 'errore' ? (
              <p className="vuoto errore-notifiche" role="alert">
                Notifiche non disponibili in questo momento.{' '}
                <button type="button" className="riprova" onClick={riprova}>Riprovate</button>
              </p>
            ) : notifiche.length === 0 ? <p className="vuoto">Nessuna notifica nelle ultime 24 ore.</p> : (
            <ol>
              {notifiche.map((n, i) => <VoceNotifica key={n.id || `${n.sent_at}-${i}`} n={n} ripiego={ripiego} chiudi={chiudi} />)}
            </ol>
          )}
        </div>
      )}
    </div>
  );
};

const MenuProfilo = () => {
  const { user, userType, logout } = useAuth();
  const { io } = usePersone();
  const navigate = useNavigate();
  const [aperto, setAperto] = useState(false);
  const ref = useRef(null);
  useEffect(() => chiudiFuori(aperto, () => setAperto(false), ref), [aperto]);
  const esci = () => { logout(); navigate('/login'); toast.success('Siete usciti'); };
  const chiudi = () => setAperto(false);
  return (
    <div className="menu-ancora" ref={ref}>
      <button type="button" className={`avatar col-${io?.colore || 'oro'}`} onClick={() => setAperto(!aperto)} aria-label="Profilo e uscita" aria-expanded={aperto}>
        {(user?.nome || '?').charAt(0).toUpperCase()}
      </button>
      {aperto && (
        <div className="tendina" role="menu">
          <div className="chi"><b>{[user?.nome, user?.cognome].filter(Boolean).join(' ')}</b><small>{user?.email}</small></div>
          <Link to="/profilo" role="menuitem" onClick={chiudi}><Icona nome="profilo" />Profilo</Link>
          {userType === 'admin' && <Link to="/admin/collaboratori" role="menuitem" onClick={chiudi}><Icona nome="persone" />Collaboratori</Link>}
          {userType === 'admin' && <Link to="/admin/numeri" role="menuitem" onClick={chiudi}><Icona nome="torta" />I numeri</Link>}
          {/* Le provvigioni: l'admin vede tutto, il collaboratore solo la sua quota (lo decide il server) */}
          <Link to="/admin/provvigioni" role="menuitem" onClick={chiudi}><Icona nome="euro" />Provvigioni</Link>
          {userType === 'admin' && <Link to="/admin/impostazioni" role="menuitem" onClick={chiudi}><Icona nome="impostazioni" />Impostazioni</Link>}
          <button type="button" role="menuitem" className="esci" onClick={esci}><Icona nome="esci" />Esci</button>
        </div>
      )}
    </div>
  );
};

const LayoutStaff = ({ children, nuovoStile = false }) => {
  const { userType } = useAuth();
  const { pathname } = useLocation();
  const [cerca, setCerca] = useState(false);
  const apriCerca = useCallback(() => setCerca(true), []);
  useScorciatoiaCerca(apriCerca);
  const voci = vociStaff(userType);
  useEffect(() => {
    document.documentElement.classList.add('mk-barra-cliente');
    return () => document.documentElement.classList.remove('mk-barra-cliente');
  }, []);
  return (
    <div className="mk-staff">
      {nuovoStile && <><div className="glow" /><div className="grain" /></>}
      <nav className="nav-staff" aria-label="Menu principale">
        <div className="wrap">
          <Link className="brand" to={voci[0].to}><img src="/mk_logo_original.png" alt="" />MK<i>.</i>Consulting<span>{userType === 'admin' ? 'Admin' : 'Staff'}</span></Link>
          <ul>
            {voci.map((v) => (
              <li key={v.to}><Link to={v.to} className={v.attiva(pathname) ? 'on' : undefined} aria-current={v.attiva(pathname) ? 'page' : undefined}>{v.etichetta}</Link></li>
            ))}
          </ul>
          <button type="button" className="cerca-btn" onClick={apriCerca} aria-label="Cerca (⌘K)" data-testid="apri-ricerca">
            <Icona nome="cerca" /><span>Cerca</span><kbd>⌘K</kbd>
          </button>
          <Campanella />
          <MenuProfilo />
        </div>
      </nav>
      <div className="sopra-barra">
        {nuovoStile ? children : <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">{children}</main>}
      </div>
      <nav className="barra-staff" aria-label="Menu in basso">
        {voci.map((v) => (
          <Link key={v.to} to={v.to} className={v.attiva(pathname) ? 'on' : undefined} aria-current={v.attiva(pathname) ? 'page' : undefined}>
            <Icona nome={v.icona} />{v.etichetta}
          </Link>
        ))}
      </nav>
      <RicercaGlobale aperta={cerca} chiudi={() => setCerca(false)} />
    </div>
  );
};

export default LayoutStaff;
